/**
 * @file
 * Old AMD key: `ac/jira/dashboard-item`
 */

import { getJQuery } from '../../../common/index.tsx';
import type { ACJSHost, ConnectCallback } from '../../../types.tsx';

/**
 * @see {@link https://stackoverflow.com/a/7053197 Source}
 * Rough equivalent of jQuery's `$(document).ready(...)`
 */
const ready = (callback: { (): void; (ev: Event): void }) => {
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	if (document.readyState !== 'loading') {
		callback();
	}

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	document.addEventListener('DOMContentLoaded', callback);
};

const resizeGadget = (target: Element, height?: string): void => {
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	if (window.dashboardSpaBridge) {
		const gadget = target.closest('[data-gadget-id]');
		if (gadget && height) {
			const gadgetId = gadget.getAttribute('data-gadget-id');

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.dashboardSpaBridge.setGadgetHeight(gadgetId, height.replace(/[^0-9.]/g, ''));
		}
	}
};

export const registerDashboardItemEvents = (connectHost: ACJSHost) => {
	connectHost.onIframeEstablished((data: { $el?: Element }) => {
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		resizeGadget(data.$el!);
	});

	ready(() => {
		getJQuery()('body').on(
			'resized',
			'.ap-container',
			(e: { target: Element }, dimensions: { height: string }) => {
				resizeGadget(e.target, dimensions.height);
			},
		);
	});
};

/**
 * Set the title of a dashboard item to the given text.
 * @memberof module:Jira
 * @method setDashboardItemTitle
 * @param {String} title - the title of the dashboard item. Any HTML is escaped.
 */
export const setDashboardItemTitle = (title: string, callback: ConnectCallback) => {
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	if (window.dashboardSpaBridge) {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		const container = document.getElementById(callback._context.extension_id);
		if (container) {
			const gadget = container.closest('[data-gadget-id]');
			if (gadget) {
				const gadgetId = gadget.getAttribute('data-gadget-id');

				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.dashboardSpaBridge.setGadgetTitle(gadgetId, title);
			}
		}
	}
};

/**
 * Returns whether the current user is permitted to edit the dashboard item
 * @memberof module:Jira
 * @method isDashboardItemEditable
 * @param {Function} callback - the callback that handles the response
 */
export const isDashboardItemEditable = (
	callback: ConnectCallback<(isEditable: boolean) => void>,
) => {
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	if (window.dashboardSpaBridge) {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		const container = document.getElementById(callback._context.extension_id);
		if (container) {
			const gadget = container.closest('[data-gadget-id]');
			if (gadget) {
				const gadgetId = gadget.getAttribute('data-gadget-id');

				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				const isEditable = window.dashboardSpaBridge.isGadgetConfigurable(gadgetId);
				callback(isEditable);
			}
		}
	}
};
